
import { db, serverTimestamp, auth } from "../../firebaseClient";
import type { LegalWorkLog, WorkLogModule } from "../types/legalWorkLog";
import type { AttachmentMeta } from "../types/attachments";
import { getRoleByEmail } from "../config/roles";
import { canViewData } from "../utils/dataAccess";

const COLLECTION = "legal_work_logs";

// Helper to construct AppUser object from Firebase Auth user
function getCurrentAppUser() {
    const u = auth.currentUser;
    if (!u) return null;
    return {
        id: u.uid,
        email: u.email || "",
        name: u.displayName || "",
        role: getRoleByEmail(u.email),
        displayName: u.displayName || ""
    };
}

// Action 中文對照 (Helper)
const ACTION_LABELS: Record<string, string> = {
  REVIEW_SUBMITTED: "送出審查",
  APPROVED: "核准",
  REJECTED: "駁回",
  CASE_CLOSED: "案件結案",
  DOC_ISSUED: "函覆送出",
  DOC_SUBMITTED: "提交定稿",
  TEMPLATE_PUBLISHED: "範本發布",
  TEMPLATE_UPDATED: "範本更新",
  STATUS_CHANGE: "狀態變更",
  ARCHIVED: "歸檔",
  PENDING: "待辦建立",
  DONE: "待辦完成",
  TASK_CREATED: "任務指派",
  MANUAL_ENTRY: "手動紀錄",
  CREATED: "建立",
  SUBMITTED: "提交",
  IMPORT: "匯入",
  INTEL_TODO: "情報待辦",
  INTEL_TODO_ARCHIVE: "情報待辦(歸檔)",
  GOV_PROJECT_TODO: "法定專案待辦",
  SIGN_SUBMITTED: "送出簽核",
  SIGN_APPROVED: "簽核通過",
  SIGN_REJECTED: "簽核駁回"
};

// Module 中文對照 (Helper)
export function getModuleLabel(code: string): string {
  const map: Record<string, string> = {
    CONTRACT_SYSTEM: "合約系統管理",
    CONTRACT_DRAFT: "合約草案審查",
    DISPUTE: "案件及爭議管理",
    IP_PROTECTION: "智財申請與維權",
    STAMP: "用印申請與紀錄",
    PUBLIC_DOC: "公文函覆",
    GOVERNANCE: "公司治理/法遵",
    TEMPLATE_CENTER: "範本中心",
    BRAND: "品牌資產管理",
    OTHER: "其他",
    // New mappings
    contract: "合約系統管理",
    case: "案件及爭議管理",
    brandAsset: "品牌資產管理",
    template: "範本中心",
    govProject: "（已下架）法規情報及法定專案", 
    REG_COMPLIANCE: "（已下架）法規情報及法定專案", 
    ip: "智財申請與維權",
    govCorrespondence: "公文函覆",
    sealRequest: "用印申請與紀錄",
    contractDraft: "合約草案審查",
    
    // New Approval Types
    LAW_INTEL: "法規情報簽核",
    GOV_PROJECT: "政府應行專案",
    GOV_OBLIGATION: "政府應行專案簽核",
    INTEL_TODO: "法規情報待辦",
    LAW_INFO: "法規情報",
    
    // New Module
    REG_IMPACT_TRACKING: "法遵風險與專案治理平台", 
    COMPLIANCE_PROJECT: "專案追蹤與簽核 (法遵)"
  };
  return map[code] || code;
}

export async function createLegalWorkLog(params: {
  userId: string;
  userName: string;
  module: string;
  recordType?: "todo" | "log"; // Default to log if not specified
  action?: string;
  refId?: string;
  refNo?: string;
  title: string;
  summary?: string; // description
  description?: string;
  
  aiSummary?: string; // New
  aiSummaryForAssignee?: string; // New
  
  status?: string;
  todoStatus?: "open" | "in_progress" | "ready_for_review" | "closed"; // New
  priority?: string;
  dueDate?: string;
  relatedDocPath?: string;
  counterpartOrDept?: string;
  finalStatus?: string;
  taskId?: string;
  extra?: any;
  attachments?: AttachmentMeta[];
  primaryDeptCode?: string;
  assignedDeptCodes?: string[];
  plannedActions?: string;
  expectedOutcome?: string;
  plannedWorkDescription?: string;
  approvalReqId?: string;
}) {
  try {
    const moduleLabel = getModuleLabel(params.module);
    const action = params.action || (params.recordType === 'todo' ? 'PENDING' : 'LOG');
    const actionLabel = ACTION_LABELS[action] || action;
    const summary = params.description || params.summary || "";
    // If it's a todo, default status is 'pending'. If it's a log, default is 'done'.
    const status = params.status || (params.recordType === 'todo' ? 'pending' : 'done');

    // Ensure no undefined values are passed to Firestore (Requirement 1 & 3)
    const payload: LegalWorkLog = {
      userId: params.userId || "unknown",
      userName: params.userName || "Unknown",
      module: params.module || "OTHER",
      moduleLabel,
      action,
      actionLabel,
      recordType: params.recordType || "log",
      status: status,
      finalStatus: params.finalStatus || (status === 'done' ? '已完成' : '待處理'),
      todoStatus: params.todoStatus,
      priority: (params.priority as any) || "Medium",
      dueDate: params.dueDate ?? "", // Use nullish coalescing
      relatedDocPath: params.relatedDocPath ?? "",
      refId: params.refId ?? "",
      refNo: params.refNo ?? "", // SafeRefNo: Ensure it's never undefined
      title: params.title || "(無標題)",
      summary: summary,
      description: summary,
      
      aiSummary: params.aiSummary,
      aiSummaryForAssignee: params.aiSummaryForAssignee,
      
      plannedActions: params.plannedActions,
      expectedOutcome: params.expectedOutcome,
      plannedWorkDescription: params.plannedWorkDescription,
      counterpartOrDept: params.counterpartOrDept ?? "",
      taskId: params.taskId ?? "",
      extra: params.extra ?? {},
      attachments: params.attachments || [],
      primaryDeptCode: params.primaryDeptCode || "",
      assignedDeptCodes: params.assignedDeptCodes || [],
      approvalReqId: params.approvalReqId,
      occurredAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Remove any explicitly undefined keys just in case
    Object.keys(payload).forEach(key => {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    });

    const docRef = await db.collection(COLLECTION).add(payload);
    console.log("[LegalWorkLog] Logged:", params.title);
    return docRef.id;
  } catch (e) {
    console.error("[LegalWorkLog] Failed to log", e);
    return null;
  }
}

export async function updateLegalWorkLog(id: string, updates: Partial<LegalWorkLog>) {
  try {
    const payload = { ...updates, updatedAt: serverTimestamp() };
    if (payload.action) {
      payload.actionLabel = ACTION_LABELS[payload.action] || payload.action;
    }
    // Sync summary aliases
    if (payload.description && !payload.summary) payload.summary = payload.description;
    if (payload.summary && !payload.description) payload.description = payload.summary;

    await db.collection(COLLECTION).doc(id).update(payload);
  } catch (e) {
    console.error("[LegalWorkLog] Update failed", e);
  }
}

export async function deleteLegalWorkLog(id: string) {
  try {
    await db.collection(COLLECTION).doc(id).delete();
  } catch (e) {
    console.error("[LegalWorkLog] Delete failed", e);
    throw e;
  }
}

export async function queryLegalWorkLogs(
  userId: string,
  startDate: string,
  endDate: string, // YYYY-MM-DD
  moduleFilter?: string,
  deptFilter?: string // New: Filter by assignedDeptCodes
): Promise<LegalWorkLog[]> {
  // Base Query: Order by time descending
  let q = db.collection(COLLECTION).orderBy("occurredAt", "desc");

  // Apply Dept Filter if provided (for dept-minimal accounts)
  if (deptFilter && deptFilter !== "ALL") {
      q = q.where("assignedDeptCodes", "array-contains", deptFilter);
  }

  // Fetch reasonable limit (500) then client filter for flexibility
  const snap = await q.limit(500).get();
  let logs = snap.docs.map(d => ({ id: d.id, ...d.data() } as LegalWorkLog));

  const currentUser = getCurrentAppUser();

  // Memory Filter
  const start = new Date(startDate + "T00:00:00").getTime();
  const end = new Date(endDate + "T23:59:59").getTime();

  logs = logs.filter(log => {
    // 1. Time Filter
    const t = log.occurredAt?.toDate ? log.occurredAt.toDate().getTime() : 0;
    if (t < start || t > end) return false;
    
    // 2. Module Filter
    if (moduleFilter && moduleFilter !== "ALL" && log.module !== moduleFilter) return false;
    
    // 3. Security Filter (Dept Scope)
    // For Logs, we treat them as documents that need authorization
    if (!canViewData(currentUser, log)) return false;

    return true;
  });

  return logs;
}
